import React, { useEffect, useRef, useState } from "react";
import { formatCurrency, formatDate, formatDecimal, formatNumber } from "../../metrics";
import { Pill } from "../../components/Ui";
import SourceTip from "../../components/SourceTip";
import { useSources } from "../../hooks/useBdApi";
import { currentEtYear } from "../../hooks/usePeriod";

// ---------------------------------------------------------------- formatting

export function formatTarget(value, format) {
  if (value === null || value === undefined || value === "") return "—";
  if (format === "currency") return formatCurrency(value);
  if (format === "percent") return `${formatDecimal(Number(value) * 100)}%`;
  return Number.isInteger(Number(value)) ? formatNumber(value) : formatDecimal(value);
}

export function formatPace(pace) {
  if (pace === null || pace === undefined || !Number.isFinite(pace)) return "—";
  return `${Math.round(pace * 100)}%`;
}

// ---------------------------------------------------------------- progress chip

const PACE_WORDS = { success: "on track", warning: "slightly behind", danger: "behind" };
const PACE_WORDS_LOWER_IS_BETTER = { success: "within budget", warning: "slightly over budget", danger: "over budget" };

/**
 * Headline progress figure for a driver: the share of the full-year goal
 * achieved ("62% of goal"). The colour is the pace tone - how the actual
 * compares with where the driver should be by now - explained in the tooltip.
 */
export function PaceChip({ row, compact = false }) {
  if (!row || row.format === "text") return null;
  const hasActual = row.actual !== null && row.actual !== undefined;
  if (!hasActual || row.attainment === null || row.attainment === undefined) {
    return (
      <span className={`bd-pace bd-pace--neutral ${compact ? "bd-pace--compact" : ""}`} title="No progress data yet">
        —
      </span>
    );
  }
  const noun = row.lowerIsBetter ? "budget" : "goal";
  const words = (row.lowerIsBetter ? PACE_WORDS_LOWER_IS_BETTER : PACE_WORDS)[row.tone];
  const lines = [`${formatPace(row.attainment)} of the full-year ${noun}: ${formatTarget(row.actual, row.format)} of ${formatTarget(row.target, row.format)}.`];
  if (words && row.snapshot) lines.push(`Colour: ${words} (current figure vs target).`);
  else if (words)
    lines.push(
      `Colour: ${words} - ${formatPace(row.pace)} of the ${formatTarget(row.expectedYtd, row.format)} expected by now (green 90%+, amber 60-90%, red under 60%).`
    );
  return (
    <span className={`bd-pace bd-pace--${row.tone} ${compact ? "bd-pace--compact" : ""}`} title={lines.join("\n")}>
      {formatPace(row.attainment)}
      <small>of {noun}</small>
    </span>
  );
}

export function AttainmentBar({ row }) {
  if (!row || row.format === "text" || row.target === null || row.target === undefined) return null;
  const pct = row.attainment === null || row.attainment === undefined ? 0 : Math.min(100, Math.round(row.attainment * 100));
  const expectedPct = row.expectedYtd && row.target ? Math.min(100, Math.round((row.expectedYtd / row.target) * 100)) : null;
  return (
    <div className="bd-attain" title={`${pct}% of goal${expectedPct !== null && !row.snapshot ? ` · marker = where you'd expect to be by now (${expectedPct}%)` : ""}`}>
      <div className={`bd-attain__fill bd-attain__fill--${row.tone}`} style={{ width: `${pct}%` }} />
      {expectedPct !== null && !row.snapshot && <div className="bd-attain__marker" style={{ left: `${expectedPct}%` }} />}
    </div>
  );
}

// ---------------------------------------------------------------- editable cell

/**
 * Inline editor for a target. Numeric formats show a number input; "text"
 * shows a textarea. Saves on blur / Enter, cancels on Escape.
 */
export function EditableCell({ value, format = "number", canEdit, onSave, placeholder = "—", align = "right", multiline = false, muted = false, title }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value === null || value === undefined ? "" : String(value));
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!editing) setDraft(value === null || value === undefined ? "" : String(value));
  }, [value, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current.select) inputRef.current.select();
    }
  }, [editing]);

  const isText = format === "text";
  const display = isText ? value || "" : formatTarget(value, format);

  const mutedClass = muted ? "bd-cell--muted" : "";

  if (!canEdit) {
    return (
      <span className={`bd-cell bd-cell--${align} ${mutedClass} ${!value && value !== 0 ? "bd-muted" : ""}`} title={title}>
        {display || placeholder}
      </span>
    );
  }

  const commit = async () => {
    if (saving) return;
    const raw = draft.trim();
    let next;
    if (isText) next = raw;
    else if (raw === "") next = null;
    else {
      const n = Number(raw.replace(/[$,%\s]/g, ""));
      if (!Number.isFinite(n)) {
        setDraft(value === null || value === undefined ? "" : String(value));
        setEditing(false);
        return;
      }
      next = n;
    }
    const current = isText ? value || "" : value === undefined ? null : value;
    if (next === current) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(next);
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        className={`bd-cell bd-cell--editable bd-cell--${align} ${mutedClass} ${!display ? "is-empty" : ""}`}
        onClick={() => setEditing(true)}
        title={title ? `${title} · click to edit` : "Click to edit"}
      >
        {display || placeholder}
      </button>
    );
  }

  if (isText || multiline) {
    return (
      <textarea
        ref={inputRef}
        className="bd-cell-input bd-cell-input--text"
        value={draft}
        rows={2}
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setDraft(value || "");
            setEditing(false);
          } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) commit();
        }}
      />
    );
  }

  return (
    <input
      ref={inputRef}
      className={`bd-cell-input bd-cell-input--${align}`}
      type="text"
      inputMode="decimal"
      value={draft}
      disabled={saving}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit();
        else if (e.key === "Escape") {
          setDraft(value === null || value === undefined ? "" : String(value));
          setEditing(false);
        }
      }}
    />
  );
}

// ---------------------------------------------------------------- year selector

export function YearSelector({ year, years = [], onChange }) {
  const options = Array.from(new Set([...(years || []), year, currentEtYear(), currentEtYear() + 1])).sort((a, b) => b - a);
  return (
    <label className="bd-year">
      <span className="bd-year__label">AGP year</span>
      <select className="bd-select" value={year} onChange={(e) => onChange(Number(e.target.value))}>
        {options.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
    </label>
  );
}

// ---------------------------------------------------------------- misc

/**
 * "auto" / "manual" / "derived" tag for an AGP driver. For auto drivers pass
 * `driverKey` to also show a SourceTip linking to the Salesforce report the
 * actual is computed from (Jan 1 of the AGP year -> today).
 */
export function SourceTag({ source, driverKey, label }) {
  const { sources } = useSources();
  const agpMetric = driverKey && sources && sources.agpDrivers ? sources.agpDrivers[driverKey] : null;
  if (source === "auto") {
    return (
      <span className="bd-agp-source">
        <Pill tone="info" title="Tracked automatically from Salesforce">
          auto
        </Pill>
        {agpMetric && <SourceTip metric={agpMetric} title={label ? `AGP - ${label}` : undefined} />}
      </span>
    );
  }
  if (source === "derived") {
    return (
      <span className="bd-agp-source">
        <Pill tone="neutral" title="Derived from other drivers">
          derived
        </Pill>
        {agpMetric && <SourceTip metric={agpMetric} title={label ? `AGP - ${label}` : undefined} />}
      </span>
    );
  }
  return <Pill tone="neutral" title="Actuals entered manually in BetterBlocks (not from Salesforce)">manual</Pill>;
}

// Human label for the AGP measurement window: always Jan 1 of the goal year
// to today (annual goals are never filtered by the performance period).
export function agpWindowLabel(window) {
  if (!window) return "";
  const from = window.from ? formatDate(window.from, { year: false }) : "Jan 1";
  const to = window.isCurrentYear === false && window.to ? formatDate(window.to) : "today";
  return `${from} – ${to}`;
}

export function ElapsedNote({ window }) {
  if (!window) return null;
  const pct = Math.round((window.elapsedFraction || 0) * 100);
  return (
    <span className="bd-muted" title="Expected by now = full-year goal x share of the year elapsed. Pace = actual / expected by now. Green: pace 90% or more; amber: 60-90%; red: under 60%.">
      Progress measured {agpWindowLabel(window)} ({pct}% of the year elapsed) · % = share of the full-year goal achieved · colour = pace vs where you'd expect to be by now (green 90%+, amber 60–90%, red under 60%)
    </span>
  );
}
